/**
 * The pieces every key shape is built from (M0.5.2).
 *
 * Three formats are fixed here that [Data-Model.md](../../../Technical/Data-Model.md)
 * left open. Each is load-bearing for *sorting*, which is the property DynamoDB
 * sort keys actually trade on, and none can be changed once records exist.
 */

/** Separates segments. Chosen by Data-Model.md §2; every key shape uses it. */
export const DELIMITER = '#';

/**
 * Sequence numbers are zero-padded to twelve digits.
 *
 * Sort keys compare as *strings*, so an unpadded `10` sorts before `9` and the
 * read-marker range queries in access pattern 5 silently return the wrong
 * rows. Twelve digits allows just under a trillion items in one feed, which is
 * not a limit anyone will meet.
 */
export const SEQ_DIGITS = 12;
export const MAX_SEQ = 10 ** SEQ_DIGITS - 1;

/** Poll-scheduling buckets, `SCHED#00` … `SCHED#63` (Data-Model.md §2). */
export const SCHED_BUCKET_DIGITS = 2;
export const MAX_SCHED_BUCKET = 63;

/**
 * Timestamps are ISO-8601 UTC with milliseconds — exactly 24 characters,
 * `YYYY-MM-DDTHH:mm:ss.sssZ`.
 *
 * Fixed width and lexicographically ordered, so `STAR#`, `ARCH#` and `ITEM#`
 * sort keys are date-ordered for free. Chosen over epoch milliseconds because
 * a human reading the DynamoDB console can tell what a record is, and the
 * width is identical either way.
 *
 * A non-UTC or second-precision timestamp would still *parse* but would sort
 * incorrectly against its neighbours, so the format is validated rather than
 * merely converted.
 */
export const TIMESTAMP_LENGTH = 24;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * Raised when a key cannot be built or read.
 *
 * M0.5.2 requires malformed keys to throw rather than return partial results:
 * a half-decoded key produces a query against the wrong partition, which reads
 * as missing data rather than as an error.
 */
export class KeyFormatError extends Error {
  constructor(
    message: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'KeyFormatError';
  }
}

/** Encodes a sequence number for use in a sort key. */
export function encodeSeq(seq: number): string {
  if (!Number.isInteger(seq) || seq < 0 || seq > MAX_SEQ) {
    throw new KeyFormatError(
      `Sequence number must be an integer between 0 and ${String(MAX_SEQ)}`,
      { seq },
    );
  }
  return String(seq).padStart(SEQ_DIGITS, '0');
}

export function decodeSeq(value: string): number {
  if (!new RegExp(`^\\d{${String(SEQ_DIGITS)}}$`).test(value)) {
    throw new KeyFormatError(
      `Sequence segment must be exactly ${String(SEQ_DIGITS)} digits`,
      { value },
    );
  }
  return Number(value);
}

export function encodeSchedBucket(bucket: number): string {
  if (!Number.isInteger(bucket) || bucket < 0 || bucket > MAX_SCHED_BUCKET) {
    throw new KeyFormatError(
      `Schedule bucket must be an integer between 0 and ${String(MAX_SCHED_BUCKET)}`,
      { bucket },
    );
  }
  return String(bucket).padStart(SCHED_BUCKET_DIGITS, '0');
}

export function decodeSchedBucket(value: string): number {
  if (!new RegExp(`^\\d{${String(SCHED_BUCKET_DIGITS)}}$`).test(value)) {
    throw new KeyFormatError('Schedule bucket segment must be exactly two digits', {
      value,
    });
  }
  const bucket = Number(value);
  if (bucket > MAX_SCHED_BUCKET) {
    throw new KeyFormatError(
      `Schedule bucket ${String(bucket)} is above the maximum of ${String(MAX_SCHED_BUCKET)}`,
      { value },
    );
  }
  return bucket;
}

/** Normalises a `Date` or ISO string to the canonical sortable form. */
export function encodeTimestamp(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    throw new KeyFormatError('Timestamp is not a valid date', { value: String(value) });
  }
  return date.toISOString();
}

export function decodeTimestamp(value: string): string {
  if (!TIMESTAMP_PATTERN.test(value)) {
    throw new KeyFormatError(
      'Timestamp segment must be ISO-8601 UTC with milliseconds (YYYY-MM-DDTHH:mm:ss.sssZ)',
      { value },
    );
  }
  return value;
}

/**
 * Escapes a free-text segment so the delimiter stays unambiguous.
 *
 * Only tags need this: every other segment is a ULID, a hex digest or a UUID.
 * A tag is whatever the user typed, and a tag containing `#` would otherwise
 * make `TAG#<tag>#<publishedAt>#<itemId>` split into the wrong number of
 * pieces — silently mis-attributing the item, or throwing on a perfectly
 * legitimate tag.
 *
 * Percent-encoding rather than rejecting `#`, because refusing a character a
 * user typed is worse than encoding it, and rather than a full URI encode,
 * because leaving spaces and accents intact keeps the console readable. `%`
 * is escaped first so decoding is unambiguous.
 */
export function encodeFreeTextSegment(value: string): string {
  if (value.length === 0) {
    throw new KeyFormatError('Free-text segment must not be empty');
  }
  return value.replaceAll('%', '%25').replaceAll(DELIMITER, '%23');
}

export function decodeFreeTextSegment(value: string): string {
  if (value.length === 0) {
    throw new KeyFormatError('Free-text segment must not be empty');
  }
  if (value.includes(DELIMITER)) {
    throw new KeyFormatError('Encoded free-text segment must not contain the delimiter', {
      value,
    });
  }
  // Decode %23 before %25, so an escaped percent cannot fabricate a delimiter.
  return value.replaceAll('%23', DELIMITER).replaceAll('%25', '%');
}

/**
 * Splits a key into exactly `expected` segments, or throws.
 *
 * `String.split` would happily return three pieces where four were required
 * and let the caller read the wrong field out of the wrong position.
 */
export function splitExactly(value: string, expected: number, context: string): string[] {
  const parts = value.split(DELIMITER);
  if (parts.length !== expected) {
    throw new KeyFormatError(
      `${context} must have exactly ${String(expected)} segments, got ${String(parts.length)}`,
      { value },
    );
  }
  return parts;
}

/** Asserts a literal prefix segment, so a `SUB#` key cannot be read as a `STAR#`. */
export function expectLiteral(actual: string, expected: string, context: string): void {
  if (actual !== expected) {
    throw new KeyFormatError(`${context} expected segment "${expected}"`, {
      actual,
    });
  }
}

/** Asserts a non-empty identifier segment that cannot contain the delimiter. */
export function expectIdentifier(value: string, context: string): string {
  if (value.length === 0) {
    throw new KeyFormatError(`${context} must not be empty`);
  }
  if (value.includes(DELIMITER)) {
    throw new KeyFormatError(`${context} must not contain "${DELIMITER}"`, { value });
  }
  return value;
}
